import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  AlertTriangle, Plus, Filter, Download, Upload, Shield, 
  TrendingUp, TrendingDown, Clock, User, CheckCircle2, 
  XCircle, AlertCircle, ChevronDown, ChevronUp, History,
  Target, Trash2, Edit, Eye, RefreshCw, Activity, BarChart3,
  ArrowUpRight, ArrowDownRight, Minus, Bell, Mail, Settings2, Send,
  Sparkles, Lightbulb, Zap, ListTodo, ArrowRight
} from 'lucide-react';

// Risk level colors and labels
const RISK_LEVELS = {
  low: { color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-400', label: 'Gering', bgLight: 'bg-green-100 dark:bg-green-900/30' },
  medium: { color: 'bg-yellow-500', textColor: 'text-yellow-700 dark:text-yellow-400', label: 'Mittel', bgLight: 'bg-yellow-100 dark:bg-yellow-900/30' },
  high: { color: 'bg-orange-500', textColor: 'text-orange-700 dark:text-orange-400', label: 'Hoch', bgLight: 'bg-orange-100 dark:bg-orange-900/30' },
  critical: { color: 'bg-red-500', textColor: 'text-red-700 dark:text-red-400', label: 'Kritisch', bgLight: 'bg-red-100 dark:bg-red-900/30' }
};

const CATEGORIES = {
  technical: { label: 'Technisch', icon: '🔧' },
  financial: { label: 'Finanziell', icon: '💰' },
  organizational: { label: 'Organisatorisch', icon: '🏢' },
  legal: { label: 'Rechtlich', icon: '⚖️' },
  operational: { label: 'Operativ', icon: '⚙️' },
  external: { label: 'Extern', icon: '🌍' },
  general: { label: 'Allgemein', icon: '📋' }
};

const STATUS_OPTIONS = {
  open: { label: 'Offen', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  monitoring: { label: 'Überwachung', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  mitigating: { label: 'Massnahmen laufend', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  mitigated: { label: 'Mitigiert', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  closed: { label: 'Geschlossen', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' },
  accepted: { label: 'Akzeptiert', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' }
};

const LIKELIHOOD_LABELS = ['', 'Sehr gering', 'Gering', 'Mittel', 'Hoch', 'Sehr hoch'];
const IMPACT_LABELS = ['', 'Minimal', 'Gering', 'Moderat', 'Schwer', 'Kritisch'];

// Risk Matrix Component
const RiskMatrix = ({ matrixData, onCellClick }) => {
  const getCellColor = (likelihood, impact) => {
    const score = likelihood * impact;
    if (score <= 4) return 'bg-green-200 dark:bg-green-900/50 hover:bg-green-300';
    if (score <= 9) return 'bg-yellow-200 dark:bg-yellow-900/50 hover:bg-yellow-300';
    if (score <= 15) return 'bg-orange-200 dark:bg-orange-900/50 hover:bg-orange-300';
    return 'bg-red-200 dark:bg-red-900/50 hover:bg-red-300';
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[400px]">
        {/* Header row with impact labels */}
        <div className="flex mb-1">
          <div className="w-24 flex-shrink-0" />
          {[1, 2, 3, 4, 5].map(impact => (
            <div key={impact} className="flex-1 text-center text-xs font-medium text-muted-foreground px-1">
              {IMPACT_LABELS[impact]}
            </div>
          ))}
        </div>
        
        {/* Matrix rows */}
        {[5, 4, 3, 2, 1].map(likelihood => (
          <div key={likelihood} className="flex mb-1">
            <div className="w-24 flex-shrink-0 text-xs font-medium text-muted-foreground flex items-center justify-end pr-2">
              {LIKELIHOOD_LABELS[likelihood]}
            </div>
            {[1, 2, 3, 4, 5].map(impact => {
              const key = `${likelihood}-${impact}`;
              const risks = matrixData?.[key] || [];
              return (
                <TooltipProvider key={key}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div 
                        className={`flex-1 aspect-square min-h-[50px] ${getCellColor(likelihood, impact)} 
                          rounded cursor-pointer transition-colors flex items-center justify-center mx-0.5`}
                        onClick={() => onCellClick && onCellClick(likelihood, impact, risks)}
                      >
                        {risks.length > 0 && (
                          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
                            {risks.length}
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    {risks.length > 0 && (
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="font-medium mb-1">{risks.length} Risiko(en)</p>
                        <ul className="text-xs space-y-1">
                          {risks.slice(0, 3).map(r => (
                            <li key={r.id}>• {r.title}</li>
                          ))}
                          {risks.length > 3 && <li>... und {risks.length - 3} weitere</li>}
                        </ul>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        ))}
        
        {/* Axis labels */}
        <div className="flex mt-2">
          <div className="w-24 flex-shrink-0" />
          <div className="flex-1 text-center text-xs text-muted-foreground font-medium">
            Auswirkung →
          </div>
        </div>
        <div className="absolute -left-6 top-1/2 transform -rotate-90 origin-center text-xs text-muted-foreground font-medium">
          Wahrscheinlichkeit →
        </div>
      </div>
    </div>
  );
};

// Risk Trend Chart Component (Simple SVG-based chart)
const RiskTrendChart = ({ trendData, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!trendData || !trendData.score_evolution || trendData.score_evolution.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Activity className="w-12 h-12 mb-4 opacity-50" />
        <p>Keine Trend-Daten verfügbar</p>
        <p className="text-sm mt-1">Risiken müssen über Zeit verändert werden, um Trends zu sehen.</p>
      </div>
    );
  }

  const { score_evolution, timeline, top_movers, status_changes, level_distribution_over_time } = trendData;

  // Prepare chart data
  const chartWidth = 600;
  const chartHeight = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Find min/max values
  const scores = score_evolution.map(d => d.avg_score);
  const minScore = Math.min(...scores, 0);
  const maxScore = Math.max(...scores, 25);
  const scoreRange = maxScore - minScore || 1;

  // Create path for line chart
  const points = score_evolution.map((d, i) => {
    const x = padding.left + (i / (score_evolution.length - 1 || 1)) * innerWidth;
    const y = padding.top + innerHeight - ((d.avg_score - minScore) / scoreRange) * innerHeight;
    return { x, y, ...d };
  });

  const linePath = points.length > 1 
    ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
    : '';

  // Area path for gradient fill
  const areaPath = points.length > 1
    ? `M ${points[0].x},${padding.top + innerHeight} L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},${padding.top + innerHeight} Z`
    : '';

  // Calculate trend direction
  const firstScore = scores[0] || 0;
  const lastScore = scores[scores.length - 1] || 0;
  const trendDirection = lastScore > firstScore ? 'up' : lastScore < firstScore ? 'down' : 'stable';
  const trendChange = lastScore - firstScore;

  // Level distribution for stacked bar
  const latestDistribution = level_distribution_over_time?.[level_distribution_over_time.length - 1];
  const totalLatest = latestDistribution 
    ? (latestDistribution.critical + latestDistribution.high + latestDistribution.medium + latestDistribution.low)
    : 0;

  return (
    <div className="space-y-6">
      {/* Trend Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Durchschn. Score</p>
                <p className="text-2xl font-bold">{lastScore.toFixed(1)}</p>
              </div>
              <div className={`p-2 rounded-full ${
                trendDirection === 'up' ? 'bg-red-100 dark:bg-red-900/30' :
                trendDirection === 'down' ? 'bg-green-100 dark:bg-green-900/30' :
                'bg-gray-100 dark:bg-gray-800'
              }`}>
                {trendDirection === 'up' ? (
                  <ArrowUpRight className="w-5 h-5 text-red-600 dark:text-red-400" />
                ) : trendDirection === 'down' ? (
                  <ArrowDownRight className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Minus className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
              </div>
            </div>
            <p className={`text-sm mt-1 ${
              trendDirection === 'up' ? 'text-red-600 dark:text-red-400' :
              trendDirection === 'down' ? 'text-green-600 dark:text-green-400' :
              'text-muted-foreground'
            }`}>
              {trendChange > 0 ? '+' : ''}{trendChange.toFixed(1)} vs. Start
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aktive Risiken</p>
                <p className="text-2xl font-bold">{score_evolution[score_evolution.length - 1]?.count || 0}</p>
              </div>
              <BarChart3 className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {score_evolution.length > 1 && score_evolution[0].count !== score_evolution[score_evolution.length - 1].count
                ? `${score_evolution[score_evolution.length - 1].count - score_evolution[0].count > 0 ? '+' : ''}${score_evolution[score_evolution.length - 1].count - score_evolution[0].count} seit Start`
                : 'Keine Änderung'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Status-Änderungen</p>
                <p className="text-2xl font-bold">{status_changes?.length || 0}</p>
              </div>
              <Activity className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {status_changes?.[0]?.transition || 'Keine Übergänge'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top Mover</p>
                <p className="text-2xl font-bold">{top_movers?.length || 0}</p>
              </div>
              <TrendingUp className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Risiken mit Score-Änderung
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Score Evolution Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Risiko-Score Entwicklung (letzte 30 Tage)
          </CardTitle>
          <CardDescription>
            Durchschnittlicher Risiko-Score über Zeit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <svg width={chartWidth} height={chartHeight} className="w-full max-w-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
              <defs>
                <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              
              {/* Grid lines */}
              {[0, 5, 10, 15, 20, 25].map(val => {
                const y = padding.top + innerHeight - ((val - minScore) / scoreRange) * innerHeight;
                return (
                  <g key={val}>
                    <line
                      x1={padding.left}
                      y1={y}
                      x2={chartWidth - padding.right}
                      y2={y}
                      stroke="currentColor"
                      strokeOpacity="0.1"
                      strokeDasharray="4,4"
                    />
                    <text
                      x={padding.left - 8}
                      y={y + 4}
                      textAnchor="end"
                      className="fill-muted-foreground text-xs"
                    >
                      {val}
                    </text>
                  </g>
                );
              })}
              
              {/* Area fill */}
              {areaPath && (
                <path d={areaPath} fill="url(#scoreGradient)" />
              )}
              
              {/* Line */}
              {linePath && (
                <path
                  d={linePath}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
              
              {/* Data points */}
              {points.map((point, i) => (
                <g key={i}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill="hsl(var(--background))"
                    stroke="hsl(var(--primary))"
                    strokeWidth="2"
                  />
                  {/* Show date labels for first, middle, last */}
                  {(i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2)) && (
                    <text
                      x={point.x}
                      y={chartHeight - 8}
                      textAnchor="middle"
                      className="fill-muted-foreground text-xs"
                    >
                      {new Date(point.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}
                    </text>
                  )}
                </g>
              ))}
            </svg>
          </div>
        </CardContent>
      </Card>

      {/* Level Distribution and Top Movers */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Level Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Aktuelle Verteilung</CardTitle>
            <CardDescription>Risiken nach Schweregrad</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {latestDistribution && totalLatest > 0 ? (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-500" />
                  <span className="text-sm flex-1">Kritisch</span>
                  <span className="font-medium">{latestDistribution.critical}</span>
                  <div className="w-20">
                    <Progress value={(latestDistribution.critical / totalLatest) * 100} className="h-2" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-orange-500" />
                  <span className="text-sm flex-1">Hoch</span>
                  <span className="font-medium">{latestDistribution.high}</span>
                  <div className="w-20">
                    <Progress value={(latestDistribution.high / totalLatest) * 100} className="h-2" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-yellow-500" />
                  <span className="text-sm flex-1">Mittel</span>
                  <span className="font-medium">{latestDistribution.medium}</span>
                  <div className="w-20">
                    <Progress value={(latestDistribution.medium / totalLatest) * 100} className="h-2" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-green-500" />
                  <span className="text-sm flex-1">Gering</span>
                  <span className="font-medium">{latestDistribution.low}</span>
                  <div className="w-20">
                    <Progress value={(latestDistribution.low / totalLatest) * 100} className="h-2" />
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Keine Verteilungsdaten</p>
            )}
          </CardContent>
        </Card>

        {/* Top Movers */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top Score-Änderungen</CardTitle>
            <CardDescription>Risiken mit den größten Bewegungen</CardDescription>
          </CardHeader>
          <CardContent>
            {top_movers && top_movers.length > 0 ? (
              <div className="space-y-2">
                {top_movers.map((mover, i) => (
                  <div key={mover.id} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                    <div className={`p-1 rounded ${
                      mover.direction === 'up' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'
                    }`}>
                      {mover.direction === 'up' ? (
                        <TrendingUp className="w-4 h-4 text-red-600 dark:text-red-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-green-600 dark:text-green-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{mover.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {mover.original_score} → {mover.current_score}
                      </p>
                    </div>
                    <Badge variant="outline" className={
                      mover.direction === 'up' ? 'text-red-600 border-red-300' : 'text-green-600 border-green-300'
                    }>
                      {mover.change > 0 ? '+' : ''}{mover.change}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Keine Score-Änderungen erfasst</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status Changes */}
      {status_changes && status_changes.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Status-Übergänge</CardTitle>
            <CardDescription>Häufigste Status-Änderungen</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {status_changes.map((change, i) => (
                <Badge key={i} variant="outline" className="py-1.5">
                  <span className="opacity-70">{change.transition}</span>
                  <span className="ml-2 font-bold">{change.count}×</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Risk Card Component
const RiskCard = ({ risk, onEdit, onDelete, onAiSuggest, onConvertToTask, users, aiLoadingRiskId }) => {
  const [expanded, setExpanded] = useState(false);
  const levelConfig = RISK_LEVELS[risk.risk_level] || RISK_LEVELS.medium;
  const statusConfig = STATUS_OPTIONS[risk.status] || STATUS_OPTIONS.open;
  const categoryConfig = CATEGORIES[risk.category] || CATEGORIES.general;
  const isAiLoading = aiLoadingRiskId === risk.id;

  return (
    <Card className={`border-l-4 ${levelConfig.color.replace('bg-', 'border-')} transition-all hover:shadow-md`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="outline" className={levelConfig.bgLight}>
                <span className={`w-2 h-2 rounded-full ${levelConfig.color} mr-1.5`} />
                {levelConfig.label} ({risk.risk_score})
              </Badge>
              <Badge variant="outline">{categoryConfig.icon} {categoryConfig.label}</Badge>
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            </div>
            
            <h3 className="font-semibold text-base mb-1 truncate">{risk.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">{risk.description}</p>
            
            {/* Quick stats */}
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                W: {LIKELIHOOD_LABELS[risk.likelihood]}
              </span>
              <span className="flex items-center gap-1">
                <Target className="w-3 h-3" />
                A: {IMPACT_LABELS[risk.impact]}
              </span>
              {risk.responsible_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {risk.responsible_name}
                </span>
              )}
              {risk.due_date && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(risk.due_date).toLocaleDateString('de-CH')}
                </span>
              )}
            </div>
            
            {/* Mitigations preview */}
            {risk.mitigations?.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <button 
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Shield className="w-3 h-3" />
                  {risk.mitigations.length} Massnahme(n)
                  {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                
                {expanded && (
                  <div className="mt-2 space-y-2">
                    {risk.mitigations.map(mit => (
                      <div key={mit.id} className="flex items-start gap-2 text-sm p-2 rounded bg-muted/50 group">
                        {mit.status === 'completed' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={mit.status === 'completed' ? 'line-through opacity-70' : ''}>
                            {mit.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {mit.responsible_name && (
                              <span className="text-xs text-muted-foreground">
                                {mit.responsible_name}
                              </span>
                            )}
                            {mit.due_date && (
                              <span className="text-xs text-muted-foreground">
                                • bis {new Date(mit.due_date).toLocaleDateString('de-CH')}
                              </span>
                            )}
                            {mit.task_id && (
                              <Badge variant="outline" className="text-xs h-5 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                                <ListTodo className="w-3 h-3 mr-1" />
                                Task erstellt
                              </Badge>
                            )}
                          </div>
                        </div>
                        {!mit.task_id && mit.status !== 'completed' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onConvertToTask && onConvertToTask(risk, mit);
                                  }}
                                >
                                  <ListTodo className="w-3 h-3 mr-1" />
                                  <span className="text-xs">Task</span>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Als Task erstellen</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={`h-8 w-8 ${isAiLoading ? 'animate-pulse' : ''} text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30`}
                    onClick={() => !isAiLoading && onAiSuggest && onAiSuggest(risk)}
                    disabled={isAiLoading}
                  >
                    {isAiLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isAiLoading ? 'KI generiert Vorschläge...' : 'KI-Massnahmen vorschlagen'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(risk)}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(risk)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Risk Management Component
const RiskManagement = ({ 
  projectId, 
  projectName, 
  users = [],
  onImportComplete,
  projectHasBcRisks,
  bcRiskCount,
  alreadyImported
}) => {
  const [risks, setRisks] = useState([]);
  const [matrixData, setMatrixData] = useState({});
  const [summary, setSummary] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  // Import status
  const [importStatus, setImportStatus] = useState(null);
  const [showImportWarning, setShowImportWarning] = useState(false);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    notify_on_critical: true,
    notify_on_high: false,
    notify_on_overdue_mitigation: true,
    score_threshold: 15,
    recipients: [],
    notify_project_lead: true,
    notify_responsible: true
  });
  const [newRecipient, setNewRecipient] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Scheduler state
  const [schedulerSettings, setSchedulerSettings] = useState({
    enabled: false,
    frequency: 'daily',
    time: '08:30',
    last_run: null
  });
  const [runningManualCheck, setRunningManualCheck] = useState(false);
  
  // AI Suggestions state
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoadingRiskId, setAiLoadingRiskId] = useState(null);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiTargetRisk, setAiTargetRisk] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    likelihood: 3,
    impact: 3,
    status: 'open',
    responsible_id: '',
    due_date: ''
  });

  // Fetch import status
  const fetchImportStatus = async () => {
    try {
      const response = await axios.get(`${API}/risk-management/projects/${projectId}/risks/import-status`);
      setImportStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch import status:', error);
    }
  };

  // Fetch trend data
  const fetchTrendData = async () => {
    setTrendLoading(true);
    try {
      const response = await axios.get(`${API}/risk-management/projects/${projectId}/risks/trend`, {
        params: { days: 90 }
      });
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to fetch trend data:', error);
    } finally {
      setTrendLoading(false);
    }
  };

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/risk-management/projects/${projectId}/notification-settings`);
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch notification settings:', error);
    }
  };

  // Save notification settings
  const saveNotificationSettings = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/risk-management/projects/${projectId}/notification-settings`, notificationSettings);
      toast.success('Benachrichtigungseinstellungen gespeichert');
      setShowNotificationDialog(false);
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    if (notificationSettings.recipients.length === 0) {
      toast.error('Bitte fügen Sie zuerst Empfänger hinzu');
      return;
    }
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/risk-management/projects/${projectId}/test-notification`);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Testbenachrichtigung');
    } finally {
      setSendingTest(false);
    }
  };

  // Add recipient
  const addRecipient = () => {
    if (!newRecipient || !newRecipient.includes('@')) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }
    if (notificationSettings.recipients.includes(newRecipient)) {
      toast.error('Diese E-Mail-Adresse ist bereits hinzugefügt');
      return;
    }
    setNotificationSettings(prev => ({
      ...prev,
      recipients: [...prev.recipients, newRecipient]
    }));
    setNewRecipient('');
  };

  // Remove recipient
  const removeRecipient = (email) => {
    setNotificationSettings(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  // Fetch scheduler settings
  const fetchSchedulerSettings = async () => {
    try {
      const response = await axios.get(`${API}/risk-management/scheduler-status`);
      setSchedulerSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler settings:', error);
    }
  };

  // Save scheduler settings
  const saveSchedulerSettings = async () => {
    try {
      await axios.put(`${API}/risk-management/scheduler-settings`, schedulerSettings);
      toast.success('Scheduler-Einstellungen gespeichert');
    } catch (error) {
      console.error('Failed to save scheduler settings:', error);
      toast.error('Fehler beim Speichern der Scheduler-Einstellungen');
    }
  };

  // Run manual check
  const runManualCheck = async () => {
    setRunningManualCheck(true);
    try {
      const response = await axios.post(`${API}/risk-management/run-scheduled-check`);
      toast.success(`${response.data.message}: ${response.data.projects_with_issues} Projekte mit Handlungsbedarf`);
      fetchSchedulerSettings(); // Refresh last_run
    } catch (error) {
      console.error('Failed to run manual check:', error);
      toast.error('Fehler bei der manuellen Überprüfung');
    } finally {
      setRunningManualCheck(false);
    }
  };

  // Generate AI mitigation suggestions
  const generateAiSuggestions = async (risk = null) => {
    setAiLoading(true);
    if (risk?.id) {
      setAiLoadingRiskId(risk.id);
    }
    setAiSuggestions(null);
    
    try {
      let response;
      if (risk && risk.id) {
        // For existing risk
        response = await axios.post(`${API}/risk-management/projects/${projectId}/risks/${risk.id}/ai-suggest`);
        setAiTargetRisk(risk);
      } else {
        // For new risk (from form data)
        response = await axios.post(`${API}/risk-management/ai/suggest-mitigations`, {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          likelihood: formData.likelihood,
          impact: formData.impact,
          existing_mitigations: []
        });
        setAiTargetRisk(null);
      }
      
      setAiSuggestions(response.data);
      setShowAiDialog(true);
    } catch (error) {
      console.error('Failed to generate AI suggestions:', error);
      toast.error(error.response?.data?.detail || 'KI-Vorschläge konnten nicht generiert werden');
    } finally {
      setAiLoading(false);
      setAiLoadingRiskId(null);
    }
  };

  // Add AI suggestion as mitigation
  const addAiSuggestionAsMitigation = async (suggestion) => {
    if (!aiTargetRisk) {
      toast.error('Bitte wählen Sie zuerst ein Risiko aus');
      return;
    }
    
    // Build description with metadata
    const priorityEmoji = suggestion.priority === 'hoch' ? '🔴' : suggestion.priority === 'mittel' ? '🟡' : '🟢';
    const metadata = `[${priorityEmoji} ${suggestion.priority} | ⏱️ ${suggestion.timeframe} | ${suggestion.type_label}]`;
    const fullDescription = `${suggestion.title}: ${suggestion.description}\n${metadata}`;
    
    try {
      await axios.post(
        `${API}/risk-management/projects/${projectId}/risks/${aiTargetRisk.id}/mitigations`,
        {
          description: fullDescription,
          responsible_id: null,
          due_date: null
        },
        { params: { user_id: 'current', user_name: 'User' } }
      );
      
      toast.success('Massnahme hinzugefügt');
      fetchData();
      
      // Remove the added suggestion from the list
      setAiSuggestions(prev => ({
        ...prev,
        suggestions: prev.suggestions.filter(s => s.id !== suggestion.id)
      }));
    } catch (error) {
      console.error('Failed to add mitigation:', error);
      toast.error('Fehler beim Hinzufügen der Massnahme');
    }
  };

  // Convert mitigation to task
  const convertMitigationToTask = async (risk, mitigation) => {
    try {
      const response = await axios.post(
        `${API}/risk-management/projects/${projectId}/risks/${risk.id}/mitigations/${mitigation.id}/to-task`,
        {
          mitigation_id: mitigation.id,
          assigned_to: mitigation.responsible_id,
          due_date: mitigation.due_date,
          priority: "medium"
        },
        { params: { user_id: 'current', user_name: 'User' } }
      );
      
      toast.success('Massnahme als Task erstellt');
      fetchData();
      
      // Optionally navigate to the task
      // navigate(`/projects/${projectId}?tab=tasks`);
    } catch (error) {
      console.error('Failed to convert mitigation to task:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Tasks');
    }
  };

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [risksRes, matrixRes, summaryRes] = await Promise.all([
        axios.get(`${API}/risk-management/projects/${projectId}/risks`),
        axios.get(`${API}/risk-management/projects/${projectId}/risks/matrix`),
        axios.get(`${API}/risk-management/projects/${projectId}/risks/summary`)
      ]);
      setRisks(risksRes.data);
      setMatrixData(matrixRes.data.matrix);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch risk data:', error);
      toast.error('Fehler beim Laden der Risikodaten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchData();
      fetchImportStatus();
      fetchNotificationSettings();
      fetchSchedulerSettings();
    }
  }, [projectId]);

  // Lazy load trend data when tab is activated
  useEffect(() => {
    if (activeTab === 'trend' && projectId && !trendData) {
      fetchTrendData();
    }
  }, [activeTab, projectId]);

  // Filtered risks
  const filteredRisks = useMemo(() => {
    return risks.filter(risk => {
      if (filterStatus !== 'all' && risk.status !== filterStatus) return false;
      if (filterCategory !== 'all' && risk.category !== filterCategory) return false;
      if (filterLevel !== 'all' && risk.risk_level !== filterLevel) return false;
      if (searchTerm && !risk.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !risk.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    });
  }, [risks, filterStatus, filterCategory, filterLevel, searchTerm]);

  // Handlers
  const handleCreateRisk = async () => {
    if (!formData.title.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/risk-management/projects/${projectId}/risks`,
        formData,
        { params: { user_id: 'current', user_name: 'User' } }
      );
      setRisks(prev => [response.data, ...prev]);
      setShowCreateDialog(false);
      resetForm();
      toast.success('Risiko erstellt');
      fetchData(); // Refresh matrix and summary
    } catch (error) {
      console.error('Failed to create risk:', error);
      toast.error('Fehler beim Erstellen des Risikos');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRisk = async () => {
    if (!selectedRisk) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.put(
        `${API}/risk-management/projects/${projectId}/risks/${selectedRisk.id}`,
        formData,
        { params: { user_id: 'current', user_name: 'User' } }
      );
      setRisks(prev => prev.map(r => r.id === selectedRisk.id ? response.data : r));
      setShowEditDialog(false);
      setSelectedRisk(null);
      resetForm();
      toast.success('Risiko aktualisiert');
      fetchData();
    } catch (error) {
      console.error('Failed to update risk:', error);
      toast.error('Fehler beim Aktualisieren des Risikos');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRisk = async (risk) => {
    if (!confirm(`Möchten Sie das Risiko "${risk.title}" wirklich löschen?`)) return;
    
    try {
      await axios.delete(`${API}/risk-management/projects/${projectId}/risks/${risk.id}`);
      setRisks(prev => prev.filter(r => r.id !== risk.id));
      toast.success('Risiko gelöscht');
      fetchData();
    } catch (error) {
      console.error('Failed to delete risk:', error);
      toast.error('Fehler beim Löschen des Risikos');
    }
  };

  const handleEditClick = (risk) => {
    setSelectedRisk(risk);
    setFormData({
      title: risk.title,
      description: risk.description,
      category: risk.category,
      likelihood: risk.likelihood,
      impact: risk.impact,
      status: risk.status,
      responsible_id: risk.responsible_id || '',
      due_date: risk.due_date || ''
    });
    setShowEditDialog(true);
  };

  // Check if should warn before import
  const handleImportClick = async () => {
    // Always fetch fresh import status before deciding
    try {
      const response = await axios.get(`${API}/risk-management/projects/${projectId}/risks/import-status`);
      const freshStatus = response.data;
      setImportStatus(freshStatus);
      
      if (freshStatus.already_imported) {
        setShowImportWarning(true);
      } else if (!freshStatus.has_business_case) {
        toast.error('Kein Business Case für dieses Projekt vorhanden');
      } else if (freshStatus.bc_risk_count === 0) {
        toast.info('Keine Risiken im Business Case vorhanden');
      } else {
        handleImportFromBusinessCase();
      }
    } catch (error) {
      // If status check fails, show error
      toast.error('Fehler beim Prüfen des Import-Status');
    }
  };

  const handleImportFromBusinessCase = async () => {
    setShowImportWarning(false);
    try {
      const response = await axios.post(
        `${API}/risk-management/projects/${projectId}/risks/import-from-business-case`,
        null,
        { params: { user_id: 'current', user_name: 'User' } }
      );
      toast.success(response.data.message);
      fetchData();
      fetchImportStatus();
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Failed to import risks:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Importieren');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'general',
      likelihood: 3,
      impact: 3,
      status: 'open',
      responsible_id: '',
      due_date: ''
    });
  };

  const calculatePreviewScore = () => formData.likelihood * formData.impact;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Summary */}
      <div className="flex flex-col gap-4">
        {/* Summary Cards - Larger and more prominent */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/30 border-blue-200 dark:border-blue-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Gesamt</p>
                  <p className="text-3xl font-bold mt-1">{summary?.total || 0}</p>
                </div>
                <div className="p-3 bg-blue-200/50 dark:bg-blue-800/50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950/50 dark:to-red-900/30 border-red-200 dark:border-red-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">Kritisch</p>
                  <p className="text-3xl font-bold mt-1">{summary?.by_level?.critical || 0}</p>
                </div>
                <div className="p-3 bg-red-200/50 dark:bg-red-800/50 rounded-full">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/50 dark:to-orange-900/30 border-orange-200 dark:border-orange-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Hoch</p>
                  <p className="text-3xl font-bold mt-1">{summary?.by_level?.high || 0}</p>
                </div>
                <div className="p-3 bg-orange-200/50 dark:bg-orange-800/50 rounded-full">
                  <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950/50 dark:to-yellow-900/30 border-yellow-200 dark:border-yellow-800">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Überfällig</p>
                  <p className="text-3xl font-bold mt-1">{summary?.overdue_mitigations || 0}</p>
                </div>
                <div className="p-3 bg-yellow-200/50 dark:bg-yellow-800/50 rounded-full">
                  <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Neues Risiko
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  onClick={handleImportClick}
                  className={importStatus?.already_imported ? 'border-green-300 dark:border-green-700' : ''}
                >
                  <Upload className="w-4 h-4 mr-2" /> 
                  Aus Business Case
                  {importStatus?.already_imported && <CheckCircle2 className="w-4 h-4 ml-1 text-green-500" />}
                  {importStatus?.bc_risk_count > 0 && !importStatus?.already_imported && (
                    <Badge variant="secondary" className="ml-1 text-xs">{importStatus.bc_risk_count}</Badge>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {!importStatus?.has_business_case ? (
                  <p>Kein Business Case vorhanden - wird bei Klick überprüft</p>
                ) : importStatus?.bc_risk_count === 0 ? (
                  <p>Keine Risiken im Business Case</p>
                ) : importStatus?.already_imported ? (
                  <p>{importStatus.imported_count} Risiko(en) bereits importiert. Erneut klicken um weitere zu importieren.</p>
                ) : (
                  <p>{importStatus.bc_risk_count} Risiko(en) aus Business Case importieren</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  onClick={() => setShowNotificationDialog(true)}
                  className={notificationSettings.enabled ? 'border-blue-300 dark:border-blue-700' : ''}
                >
                  <Bell className="w-4 h-4 mr-2" /> 
                  Benachrichtigungen
                  {notificationSettings.enabled && <CheckCircle2 className="w-4 h-4 ml-1 text-blue-500" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{notificationSettings.enabled ? 'E-Mail-Benachrichtigungen aktiv' : 'E-Mail-Benachrichtigungen konfigurieren'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Import Warning Dialog */}
      <Dialog open={showImportWarning} onOpenChange={setShowImportWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Risiken bereits importiert
            </DialogTitle>
            <DialogDescription>
              Es wurden bereits <strong>{importStatus?.imported_count} Risiko(en)</strong> aus dem Business Case importiert.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm">
              Wenn Sie fortfahren, werden <strong>{importStatus?.bc_risk_count} weitere Risiko(en)</strong> aus dem 
              Business Case importiert. Dies kann zu Duplikaten führen.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportWarning(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="default" 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleImportFromBusinessCase}
            >
              Trotzdem importieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list">Liste</TabsTrigger>
          <TabsTrigger value="matrix">Risiko-Matrix</TabsTrigger>
          <TabsTrigger value="trend">
            <Activity className="w-4 h-4 mr-1" />
            Trend
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input 
                placeholder="Risiken durchsuchen..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {Object.entries(STATUS_OPTIONS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {Object.entries(CATEGORIES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.icon} {val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Level</SelectItem>
                {Object.entries(RISK_LEVELS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Risk List */}
          {filteredRisks.length === 0 ? (
            <Card className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Risiken gefunden</p>
              <Button className="mt-4" onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Erstes Risiko erstellen
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredRisks.map(risk => (
                <RiskCard 
                  key={risk.id} 
                  risk={risk} 
                  users={users}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteRisk}
                  onAiSuggest={generateAiSuggestions}
                  onConvertToTask={convertMitigationToTask}
                  aiLoadingRiskId={aiLoadingRiskId}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="matrix">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" /> Risiko-Matrix
              </CardTitle>
              <CardDescription>
                Klicken Sie auf ein Feld, um die Risiken zu sehen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RiskMatrix 
                matrixData={matrixData}
                onCellClick={(l, i, risks) => {
                  if (risks.length > 0) {
                    setFilterLevel('all');
                    setFilterStatus('all');
                    setFilterCategory('all');
                    setSearchTerm(risks[0].title.substring(0, 10));
                    setActiveTab('list');
                  }
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend">
          <RiskTrendChart 
            trendData={trendData} 
            loading={trendLoading}
          />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          setSelectedRisk(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? 'Risiko bearbeiten' : 'Neues Risiko erstellen'}
            </DialogTitle>
            <DialogDescription>
              Definieren Sie die Risikoeigenschaften und Bewertung
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">Titel *</Label>
              <Input 
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="z.B. Technisches Risiko: API-Ausfälle"
              />
            </div>
            
            {/* Description */}
            <div>
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea 
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detaillierte Beschreibung des Risikos..."
                rows={3}
              />
            </div>
            
            {/* Category and Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kategorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORIES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.icon} {val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_OPTIONS).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Likelihood and Impact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Wahrscheinlichkeit: {LIKELIHOOD_LABELS[formData.likelihood]}</Label>
                <input 
                  type="range" 
                  min="1" 
                  max="5" 
                  value={formData.likelihood}
                  onChange={(e) => setFormData(prev => ({ ...prev, likelihood: parseInt(e.target.value) }))}
                  className="w-full mt-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Sehr gering</span>
                  <span>Sehr hoch</span>
                </div>
              </div>
              <div>
                <Label>Auswirkung: {IMPACT_LABELS[formData.impact]}</Label>
                <input 
                  type="range" 
                  min="1" 
                  max="5" 
                  value={formData.impact}
                  onChange={(e) => setFormData(prev => ({ ...prev, impact: parseInt(e.target.value) }))}
                  className="w-full mt-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Minimal</span>
                  <span>Kritisch</span>
                </div>
              </div>
            </div>
            
            {/* Risk Score Preview */}
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex items-center justify-between">
                <span className="font-medium">Risiko-Score</span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">{calculatePreviewScore()}</span>
                  <Badge className={RISK_LEVELS[
                    calculatePreviewScore() <= 4 ? 'low' :
                    calculatePreviewScore() <= 9 ? 'medium' :
                    calculatePreviewScore() <= 15 ? 'high' : 'critical'
                  ].bgLight}>
                    {RISK_LEVELS[
                      calculatePreviewScore() <= 4 ? 'low' :
                      calculatePreviewScore() <= 9 ? 'medium' :
                      calculatePreviewScore() <= 15 ? 'high' : 'critical'
                    ].label}
                  </Badge>
                </div>
              </div>
              <Progress 
                value={(calculatePreviewScore() / 25) * 100} 
                className="mt-2 h-2"
              />
            </div>
            
            {/* Responsible and Due Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Verantwortlich</Label>
                <Select value={formData.responsible_id || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, responsible_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Person wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Niemand</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fälligkeit</Label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}>
              Abbrechen
            </Button>
            <Button 
              onClick={showEditDialog ? handleUpdateRisk : handleCreateRisk}
              disabled={isSubmitting}
            >
              {isSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              {showEditDialog ? 'Speichern' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Settings Dialog */}
      <Dialog open={showNotificationDialog} onOpenChange={setShowNotificationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              E-Mail-Benachrichtigungen
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie automatische E-Mail-Benachrichtigungen für kritische Risiken
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Main Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${notificationSettings.enabled ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                  <Mail className={`w-5 h-5 ${notificationSettings.enabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-medium">Benachrichtigungen aktivieren</p>
                  <p className="text-sm text-muted-foreground">E-Mails bei kritischen Risiken senden</p>
                </div>
              </div>
              <Button 
                variant={notificationSettings.enabled ? "default" : "outline"}
                size="sm"
                onClick={() => setNotificationSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
              >
                {notificationSettings.enabled ? 'Aktiv' : 'Inaktiv'}
              </Button>
            </div>

            {/* Trigger Settings */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Benachrichtigen bei:</Label>
              
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-muted/50">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.notify_on_critical}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, notify_on_critical: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="font-medium">Kritische Risiken</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Bei Score ≥ 16 (Kritisch)</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-muted/50">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.notify_on_high}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, notify_on_high: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500" />
                      <span className="font-medium">Hohe Risiken</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Bei Score 10-15 (Hoch)</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-muted/50">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.notify_on_overdue_mitigation}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, notify_on_overdue_mitigation: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-yellow-500" />
                      <span className="font-medium">Überfällige Massnahmen</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Wenn Fälligkeitsdatum überschritten</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Score Threshold */}
            <div className="space-y-2">
              <Label>Score-Schwellenwert für Benachrichtigung</Label>
              <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="5" 
                  max="25" 
                  value={notificationSettings.score_threshold}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, score_threshold: parseInt(e.target.value) }))}
                  className="flex-1"
                />
                <span className="font-bold text-lg w-8 text-center">{notificationSettings.score_threshold}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Benachrichtigung wenn Score ≥ {notificationSettings.score_threshold}
              </p>
            </div>

            {/* Auto Recipients */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Automatische Empfänger:</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.notify_project_lead}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, notify_project_lead: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span>Projektleiter</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input 
                    type="checkbox" 
                    checked={notificationSettings.notify_responsible}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, notify_responsible: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <span>Verantwortliche Person (pro Risiko)</span>
                </label>
              </div>
            </div>

            {/* Manual Recipients */}
            <div className="space-y-2">
              <Label>Zusätzliche Empfänger</Label>
              <div className="flex gap-2">
                <Input 
                  type="email"
                  placeholder="email@beispiel.ch"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                />
                <Button variant="outline" size="icon" onClick={addRecipient}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {notificationSettings.recipients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {notificationSettings.recipients.map((email, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1 py-1">
                      <Mail className="w-3 h-3" />
                      {email}
                      <button 
                        onClick={() => removeRecipient(email)}
                        className="ml-1 hover:text-destructive"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Scheduler Settings */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Automatische Zusammenfassung
              </Label>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="font-medium text-sm">Tägliche E-Mail-Zusammenfassung</p>
                  <p className="text-xs text-muted-foreground">
                    Sendet täglich um {schedulerSettings.time || '08:30'} Uhr eine Übersicht
                  </p>
                </div>
                <Button 
                  variant={schedulerSettings.enabled ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    const newEnabled = !schedulerSettings.enabled;
                    setSchedulerSettings(prev => ({ ...prev, enabled: newEnabled }));
                    // Auto-save scheduler settings
                    axios.put(`${API}/risk-management/scheduler-settings`, { enabled: newEnabled })
                      .then(() => toast.success(newEnabled ? 'Tägliche Zusammenfassung aktiviert' : 'Tägliche Zusammenfassung deaktiviert'))
                      .catch(() => toast.error('Fehler beim Speichern'));
                  }}
                >
                  {schedulerSettings.enabled ? 'Aktiv' : 'Inaktiv'}
                </Button>
              </div>

              {schedulerSettings.enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Uhrzeit</Label>
                    <Select 
                      value={schedulerSettings.time || '08:30'} 
                      onValueChange={(v) => {
                        setSchedulerSettings(prev => ({ ...prev, time: v }));
                        axios.put(`${API}/risk-management/scheduler-settings`, { time: v });
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="07:00">07:00</SelectItem>
                        <SelectItem value="08:00">08:00</SelectItem>
                        <SelectItem value="08:30">08:30</SelectItem>
                        <SelectItem value="09:00">09:00</SelectItem>
                        <SelectItem value="12:00">12:00</SelectItem>
                        <SelectItem value="18:00">18:00</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Frequenz</Label>
                    <Select 
                      value={schedulerSettings.frequency || 'daily'} 
                      onValueChange={(v) => {
                        setSchedulerSettings(prev => ({ ...prev, frequency: v }));
                        axios.put(`${API}/risk-management/scheduler-settings`, { frequency: v });
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Täglich</SelectItem>
                        <SelectItem value="weekly">Wöchentlich (Mo)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {schedulerSettings.last_run && (
                <p className="text-xs text-muted-foreground">
                  Letzte Ausführung: {new Date(schedulerSettings.last_run).toLocaleString('de-CH')}
                </p>
              )}

              <Button 
                variant="outline" 
                size="sm"
                className="w-full"
                onClick={runManualCheck}
                disabled={runningManualCheck}
              >
                {runningManualCheck ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Jetzt Zusammenfassung senden
              </Button>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={sendTestNotification}
              disabled={sendingTest || notificationSettings.recipients.length === 0}
              className="w-full sm:w-auto"
            >
              {sendingTest ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Test senden
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => setShowNotificationDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={saveNotificationSettings} disabled={isSubmitting}>
                {isSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                Speichern
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Suggestions Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              KI-Massnahmenvorschläge
            </DialogTitle>
            <DialogDescription>
              {aiTargetRisk ? (
                <>Vorschläge für: <span className="font-medium">{aiTargetRisk.title}</span></>
              ) : (
                'Basierend auf dem Risikoprofil generierte Empfehlungen'
              )}
            </DialogDescription>
          </DialogHeader>
          
          {aiLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-500 mb-4" />
              <p className="text-muted-foreground">KI analysiert das Risiko...</p>
              <p className="text-xs text-muted-foreground mt-1">Dies kann einige Sekunden dauern</p>
            </div>
          ) : aiSuggestions ? (
            <div className="space-y-4">
              {/* Risk Context */}
              {aiSuggestions.risk_context && (
                <div className="p-3 rounded-lg bg-muted/50 border">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Target className="w-4 h-4" />
                      Score: <span className="font-bold">{aiSuggestions.risk_context.score}</span>
                    </span>
                    <Badge variant="outline" className={
                      aiSuggestions.risk_context.level === 'kritisch' ? 'border-red-300 text-red-600' :
                      aiSuggestions.risk_context.level === 'hoch' ? 'border-orange-300 text-orange-600' :
                      'border-yellow-300 text-yellow-600'
                    }>
                      {aiSuggestions.risk_context.level}
                    </Badge>
                    <span className="text-muted-foreground">
                      Kategorie: {CATEGORIES[aiSuggestions.risk_context.category]?.label || aiSuggestions.risk_context.category}
                    </span>
                  </div>
                </div>
              )}

              {/* Summary */}
              {aiSuggestions.summary && (
                <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm">{aiSuggestions.summary}</p>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Empfohlene Massnahmen ({aiSuggestions.suggestions?.length || 0})
                </h4>
                
                {aiSuggestions.suggestions?.map((suggestion, index) => (
                  <div 
                    key={suggestion.id} 
                    className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 text-xs font-bold">
                            {index + 1}
                          </span>
                          <h5 className="font-medium">{suggestion.title}</h5>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{suggestion.description}</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge variant="outline" className={
                            suggestion.priority === 'hoch' ? 'border-red-300 text-red-600 dark:border-red-700 dark:text-red-400' :
                            suggestion.priority === 'mittel' ? 'border-yellow-300 text-yellow-600 dark:border-yellow-700 dark:text-yellow-400' :
                            'border-green-300 text-green-600 dark:border-green-700 dark:text-green-400'
                          }>
                            {suggestion.priority === 'hoch' ? '🔴' : suggestion.priority === 'mittel' ? '🟡' : '🟢'} {suggestion.priority}
                          </Badge>
                          <Badge variant="outline">
                            <Clock className="w-3 h-3 mr-1" />
                            {suggestion.timeframe}
                          </Badge>
                          <Badge variant="secondary">
                            <Zap className="w-3 h-3 mr-1" />
                            {suggestion.type_label}
                          </Badge>
                        </div>
                      </div>
                      
                      {aiTargetRisk && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-shrink-0 border-purple-300 text-purple-600 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-950/30"
                          onClick={() => addAiSuggestionAsMitigation(suggestion)}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Hinzufügen
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {aiSuggestions.suggestions?.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p>Alle Vorschläge wurden bereits hinzugefügt!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-3" />
              <p>Keine Vorschläge verfügbar</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiDialog(false)}>
              Schliessen
            </Button>
            {aiTargetRisk && !aiLoading && (
              <Button 
                variant="outline"
                onClick={() => generateAiSuggestions(aiTargetRisk)}
                disabled={aiLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${aiLoading ? 'animate-spin' : ''}`} />
                Neu generieren
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiskManagement;
